import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRelative } from '@/shared/lib/intl';
import { notificationIcon, notificationIconColor, notificationText } from '@/shared/lib/notification-display';
import type { Notification } from '@/shared/services/notifications.service';

interface NotificationRowProps {
  notification: Notification;
  onOpen: (n: Notification) => void;
  /** The brand in view: an item from another brand carries a chip naming its brand. */
  currentWorkspaceId?: string | null;
  /** Something before the row's button (the history's select box). */
  leading?: ReactNode;
}

/**
 * One notification, as the bell and the history show it: icon, title,
 * body, when, the brand when it is not the one in view, and an unread dot
 * that is also said in words (N20: unread was colour only).
 */
export function NotificationRow({ notification: n, onOpen, currentWorkspaceId, leading }: NotificationRowProps): ReactElement {
  const { t, i18n } = useTranslation();
  const Icon = notificationIcon(n.type);
  const { title, body } = notificationText(n, t, i18n);
  const otherBrand = n.workspaceId && currentWorkspaceId && n.workspaceId !== currentWorkspaceId ? n.workspaceName : null;
  return (
    <div className={['flex items-stretch border-b border-[var(--line)] last:border-b-0', n.read ? '' : 'bg-[var(--color-surface-2)]'].join(' ')}>
      {leading}
      <button
        type="button"
        onClick={() => onOpen(n)}
        className="flex-1 min-w-0 flex gap-3 px-4 py-3 min-h-[44px] text-start bg-transparent border-none cursor-pointer hover:bg-[var(--color-surface-3)] focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-accent-text)]"
        data-testid="notification-row"
      >
        <Icon size={16} className="mt-0.5 shrink-0" style={{ color: notificationIconColor(n.type) }} strokeWidth={1.5} aria-hidden="true" />
        <span className="flex-1 min-w-0">
          {!n.read && <span className="sr-only">{t('notifications.center.unreadLabel')}</span>}
          <span className="block text-ui font-semibold text-[var(--ink)] leading-tight" dir="auto">{title}</span>
          <span className="block text-meta text-[var(--ink-2)] leading-[1.4] mt-0.5 line-clamp-2" dir="auto">{body}</span>
          <span className="flex items-center gap-2 flex-wrap mt-1">
            <span className="text-caption text-[var(--ink-3)]">{formatRelative(n.createdAt)}</span>
            {otherBrand && (
              <span
                className="text-caption text-[var(--ink-2)] border border-[var(--line)] rounded-full px-2 py-px max-w-[16ch] truncate"
                title={t('notifications.center.fromBrand', { brand: otherBrand })}
                dir="auto"
              >
                {otherBrand}
              </span>
            )}
          </span>
        </span>
        {!n.read && <span className="w-2 h-2 rounded-full bg-[var(--color-accent)] shrink-0 mt-2" aria-hidden="true" />}
      </button>
    </div>
  );
}
